// ============================================================================
// ui/status-bar.js — inline imperative guidance + toast + aria-live (crossover §4)
// ============================================================================
// Carries the Gym Designer's status-bar imperative style VERBATIM in spirit:
// after every action the bar shows the next imperative step ("Drop your logo —
// PNG with transparency works best"). Doubles as the toast channel: blue flash =
// success, orange = error (crossover §2 status-bar-toast). The a11y floor (V2
// §11) needs design-change announcements → a visually-hidden aria-live region
// lives here too, so "Logo applied — preview updated" reaches screen readers.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import { labelWithDots } from './busy-dots.js';

/** How long work must already have taken before the busy pill appears. Below this the pill would
 *  flash and vanish on a fast upload, which reads as a glitch — see `busy()`. */
const BUSY_DELAY_MS = 550;

export function createStatusBar() {
  const message = h('span', { class: 'wd-status-msg' }, '');
  // SINGLE polite live region for all announcements. The container is NOT also a
  // live region (was `role="status"`) — two nested live regions announced the
  // same text twice (WD-27). Toasts route through `announce()` too so they still
  // reach AT, once.
  const live = h('span', { class: 'wd-visually-hidden', 'aria-live': 'polite', 'aria-atomic': 'true' });

  const el = h('div', { class: 'wd-status-bar wd-glass-panel' }, message, live);

  let flashTimer = null;
  let busyTimer = null;
  let busyText = '';
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-08-26) — the pill is silenced while a shared design
     is being PRESENTED. Ryan, arrowing onto the second model: *"a notification pill appears at the
     top saying 'Design opened'. Don't need any of these pills appearing on presentation mode on
     desktop or mobile."*

     ⛔ MUTED AT THE FUNNEL, NOT AT THE ONE CALLER. The pill he saw comes from `afterHistoryNav`,
     which every wholesale restore goes through — but silencing that single call would leave the
     next toast to find its way onto a customer's presentation, and there are a dozen callers. One
     switch on the surface answers for all of them, including ones written later.

     ⚠️ ONLY THE PICTURE IS MUTED. `announce()` is untouched, so a screen-reader user still hears
     what changed — they have no plate to look at, and taking the words away would leave them with
     nothing at all. Muting is about a pill covering a design someone was SENT, not about withholding
     the fact. */
  let muted = false;

  /**
   * Standing guidance is AUDIO-ONLY now (Ryan 2026-07-11: the persistent tip
   * pill covered the plate, especially on mobile — removed). The text still
   * reaches screen readers via the live region; sighted users get the same
   * cues from the panel copy itself. The pill element only becomes visible
   * during transient success/error flashes below.
   */
  function guide(text) {
    live.textContent = text;
    el.classList.remove('is-success', 'is-error', 'is-info');
  }

  /* Success flash (blue) — transient confirmation.
     1600 → 3200ms (Ryan 2026-08-08: *"the words pop up and disappear so fast that you barely have
     time to read them anyway… we need better timing"*). He is right and it is measurable: these
     messages run 6–10 words, which is ~2.7s of reading at an ordinary 200 wpm, so 1.6s expired
     while the sentence was still being read — and that is before the fade eats the tail of it.
     Matched to `error`'s 3200 rather than picked fresh, so the pill has two durations to reason
     about (a normal flash, and `info`'s slightly longer 3600 nudge) instead of three. */
  function success(text) {
    flash(text, 'is-success', 3200);
  }

  /** Error flash (orange) — hard errors; linger longer. */
  function error(text) {
    flash(text, 'is-error', 3200);
  }

  /**
   * Info nudge (blue) — a friendly "one more thing" prompt, not a scolding error
   * (Fable QW-4 §5.6). Lingers like an error so it's readable, but reads calm.
   * `opts.actionLabel` + `opts.onAction` add an inline text-button (§4.3's
   * "Adjusted your colors to pop on black — Undo") that dismisses the toast
   * early when clicked; `opts.ms` overrides the default linger.
   */
  function info(text, opts = {}) {
    flash(text, 'is-info', opts.ms || 3600, opts.actionLabel ? { label: opts.actionLabel, onAction: opts.onAction } : null);
  }

  function flash(text, cls, ms, action) {
    if (muted) return;              // presentation mode — see `muted` above
    const nodes = [text];
    if (action) {
      nodes.push(
        h(
          'button',
          {
            type: 'button',
            class: 'wd-toast-action',
            onClick: () => {
              action.onAction?.();
              clearTimeout(flashTimer);
              el.classList.remove(cls);
            },
          },
          action.label,
        ),
      );
    }
    replaceChildren(message, nodes);
    announce(text); // the container is no longer a live region → announce once here (WD-27)
    el.classList.remove('is-success', 'is-error', 'is-info');
    // Force reflow so re-adding the class re-triggers the CSS flash animation.
    void el.offsetWidth;
    el.classList.add(cls);
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => el.classList.remove(cls), ms);
  }

  /**
   * BUSY — the pill's third mode, and the only one that persists (Ryan 2026-07-29).
   *
   * *"Should a particular logo ever take a few seconds extra to process, or should someone be on a
   * device which lags, should we have the 'Enhancing your logo…' text appear on the main window so
   * people don't think nothing is happening and click off?"*
   *
   * Yes — and it belongs HERE rather than as new chrome, because the pill already floats top-centre of
   * the canvas. Note this does not reopen the 2026-07-11 decision that removed standing guidance for
   * covering the plate: that was permanent text with no end. This appears only while real work is in
   * flight and takes itself away.
   *
   * ⚠ DELAY-GATED, which is the whole design. A fast logo must never flash a pill — that reads as a
   * glitch and it is the common case. Nothing is shown until the work has already outlasted
   * `BUSY_DELAY_MS`, so the indicator exists exactly when Ryan's scenario does (a slow file, or a slow
   * device) and never otherwise. Calling `busy()` and finishing quickly is therefore a no-op by design.
   *   Announced to AT immediately though, regardless of the delay — a screen-reader user gets no
   * spinner to look at, so withholding it from them buys nothing.
   */
  function busy(text) {
    busyText = text;
    announce(text);                 // always — the announcement is not the pill
    if (muted) return;
    clearTimeout(busyTimer);
    busyTimer = setTimeout(() => {
      if (busyText !== text) return; // superseded or already finished
      replaceChildren(message, [labelWithDots(text)]);
      el.classList.remove('is-success', 'is-error', 'is-info');
      el.classList.add('is-busy');
    }, BUSY_DELAY_MS);
  }

  /** Take the busy pill away. Safe to call when it never appeared. */
  function clearBusy() {
    busyText = '';
    clearTimeout(busyTimer);
    el.classList.remove('is-busy');
  }

  /**
   * Announce a design change to assistive tech (V2 §11 aria-live). Clearing then
   * setting on the next frame guarantees repeat identical messages re-fire.
   */
  function announce(text) {
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = text;
    });
  }

  /**
   * Silence the VISIBLE pill (presentation mode). Clears anything already on screen, so a toast
   * fired a frame before the presentation opened cannot be left stranded over the plate.
   * @param {boolean} on
   */
  function setMuted(on) {
    muted = !!on;
    if (!muted) return;
    clearTimeout(flashTimer);
    clearTimeout(busyTimer);
    busyText = '';
    el.classList.remove('is-success', 'is-error', 'is-info', 'is-busy');
  }

  return { el, guide, success, error, info, announce, busy, clearBusy, setMuted };
}
